import { ActionTree, GetterTree, MutationTree } from 'vuex'
import { Location } from 'vue-router'
import { State as Root } from '.'
import leads from '@/http/leads'
import cdn from '@/http/cdn'

export interface FormContent {
  school: string
  area: string
  size: string
  chief: string
  phone: string
}

export interface NavigateTarget {
  type: 'navigate'
  id: string
  replace?: boolean
}
export interface RouterTarget {
  type: 'router'
  to: Location
}
export interface ExternalTarget {
  type: 'external'
  url: string
}
export interface CustomEvent {
  type: 'event'
  name: string
  args?: any[]
}
export type Target =
  | NavigateTarget
  | RouterTarget
  | ExternalTarget
  | CustomEvent
export interface Block {
  src?: string
  css?: string
  target?: Target
}
export type BlockElement = string | Block

export interface Page {
  id: string
  elements: BlockElement[]
}

export interface Site {
  code: string
  title: string
  pages: Page[]
  index?: number
  stylesheet?: string
}

export interface State {
  config?: Site
  channel?: string
  index?: number
  sizes: string[]
}

export const state: Action<State> = () => ({
  config: null,
  channel: null,
  index: null,
  sizes: ['100人以内', '100-200人', '200-300人', '300-500人', '500人以上']
})

export const getters: GetterTree<State, Root> = {
  pages: ({ config }) => config && config.pages,
  page: ({ index }, { pages }) =>
    pages && pages.length && index != null ? pages[index] : null
}

export const mutations: MutationTree<State> = {
  setIndex(state, index: number) {
    state.index = index
  },
  setChannel(state, channel: string) {
    state.channel = channel
  },
  setConfig(state, config: Site) {
    state.config = config
    state.index = config && config.index
  }
}

export const actions: ActionTree<State, Root> = {
  /**
   * 获取数据
   * @param url 配置文件路径
   */
  async fetch({ commit }, url: string) {
    if (!url) {
      throw new Error('路径未提供')
    }
    const res: any = await cdn.get<Site>(url, {
      params: {
        ver: Date.now()
      }
    })
    commit('setConfig', res)
  },
  /**
   * 提交表单
   * @param data 表单数据
   */
  async submit(
    { getters: { page }, state: { config, channel } },
    data: FormContent
  ) {
    if (!page) {
      return
    }
    await leads.post(
      '/form/addForm',
      JSON.stringify({
        templateId: config.code,
        contestId: page.id,
        school: data.school,
        area: data.area,
        size: data.size,
        chief: data.chief,
        phone: data.phone,
        channel
      }),
      {
        headers: {
          'content-type': 'text/plain'
        }
      }
    )
  },
  /**
   * 按索引选择页面
   * @param index 页面索引
   */
  select({ commit, getters: { pages } }, index: number) {
    if (!pages) {
      return
    }
    if (index < 0 || index >= pages.length) {
      throw new Error('要查看的对象不存在')
    }
    commit('setIndex', index)
  },
  /**
   * 取消选择(回首页)
   */
  deselect({ dispatch, state: { config } }) {
    return dispatch('select', config && config.index)
  },
  /**
   * 按ID选择页面
   * @param id 页面ID
   */
  pick({ commit, getters: { pages } }, id: string) {
    if (!pages) {
      return
    }
    const index = pages.findIndex(e => e.id === id)
    if (index < 0) {
      throw new Error('要查看的对象不存在')
    }
    commit('setIndex', index)
  }
}

export const namespaced = true
